import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Brush, ImageOff, ImageUp, PaintBucket, Pipette, SquareDashed, Trash2, Undo2, type LucideIcon,
} from 'lucide-react';
import {
  FLAG_H, FLAG_PALETTE, FLAG_SIZE, FLAG_W, FLAG_WHITE, decodeFlag, encodeFlag, nearestPaletteIndex,
} from '../constants';
import { bresenham } from '../map/coords';
import { toast } from '../worlds.store';
import { Kbd, cx } from './ui';
import styles from '../../LootboxPage.module.scss';
import ws from '../WorldsPage.module.scss';
import cs from './countries.module.scss';

// ============================================================
// Éditeur de drapeau 26 x 13 sur la palette du mod (18 couleurs, ordre exact)
// ============================================================
// Pinceau (un trait = un pas d'annulation), remplissage 4-connexe, sélection rectangle puis clic
// dans la palette pour la remplir, pipette. Raccourcis B, G, S, I et Ctrl+Z (50 annulations),
// actifs seulement quand la modale de l'éditeur est au premier plan et hors saisie.
// Import d'image : étirée en 26x13, chaque pixel ramené à la couleur de palette la plus proche.
// Sortie : base64 de 338 octets (index de palette), ou null (« Aucun drapeau », `nullable`).
// ============================================================

type Tool = 'brush' | 'bucket' | 'select' | 'pick';

interface Sel { c0: number; r0: number; c1: number; r1: number }
interface Cell { c: number; r: number }
/** Un pas d'annulation : les pixels, et l'absence de drapeau (null) à ce moment-là. */
interface Snapshot { px: Uint8Array; none: boolean }

const MAX_UNDO = 50;

const TOOLS: { key: Tool; label: string; shortcut: string; icon: LucideIcon; effect: string }[] = [
  { key: 'brush', label: 'Pinceau', shortcut: 'B', icon: Brush, effect: 'peint au clic et au glisser.' },
  { key: 'bucket', label: 'Remplissage', shortcut: 'G', icon: PaintBucket, effect: 'remplit la zone de même couleur (4 voisins).' },
  { key: 'select', label: 'Sélection', shortcut: 'S', icon: SquareDashed, effect: 'glisse pour sélectionner, puis clique une couleur de la palette.' },
  { key: 'pick', label: 'Pipette', shortcut: 'I', icon: Pipette, effect: 'adopte la couleur d\'une case, puis repasse au pinceau.' },
];

function normSel(s: Sel): Sel {
  return { c0: Math.min(s.c0, s.c1), c1: Math.max(s.c0, s.c1), r0: Math.min(s.r0, s.r1), r1: Math.max(s.r0, s.r1) };
}

/** La modale qui contient l'éditeur est-elle celle du dessus (pas de confirmation par-dessus) ? */
function editorOnTop(el: HTMLElement | null): boolean {
  if (!el) return false;
  const modals = document.querySelectorAll('[data-worlds-modal]');
  const top = modals[modals.length - 1];
  return !top || top.contains(el);
}

export function FlagEditor({ value, onChange, readOnly = false, nullable = false }: {
  /** base64 du drapeau ; null = aucun drapeau (blanc en jeu). */
  value: string | null;
  onChange: (b64: string | null) => void;
  readOnly?: boolean;
  /** Propose « Aucun drapeau » (valeur null). */
  nullable?: boolean;
}) {
  const [pixels, setPixels] = useState<Uint8Array>(() => decodeFlag(value));
  const [tool, setTool] = useState<Tool>('brush');
  const [color, setColor] = useState(0);
  const [sel, setSel] = useState<Sel | null>(null);
  const [hover, setHover] = useState<Cell | null>(null);
  // Historique dans une ref (pas d'effet de bord dans un updater de state) ; le compteur sert à l'affichage.
  const historyRef = useRef<Snapshot[]>([]);
  const [histLen, setHistLen] = useState(0);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const gridRef = useRef<HTMLDivElement | null>(null);
  const dragRef = useRef<{ last: Cell | null; selecting: boolean } | null>(null);
  const pixelsRef = useRef(pixels);
  pixelsRef.current = pixels;
  const fileRef = useRef<HTMLInputElement | null>(null);
  // Dernière valeur émise : une valeur différente reçue du parent (réouverture, remise à zéro)
  // recharge la grille.
  const emittedRef = useRef<string | null>(value);
  const noneRef = useRef(value === null);
  noneRef.current = value === null;

  useEffect(() => {
    if (value === emittedRef.current) return;
    emittedRef.current = value;
    setPixels(decodeFlag(value));
  }, [value]);

  const commit = useCallback((next: Uint8Array) => {
    setPixels(next);
    const b64 = encodeFlag(next);
    emittedRef.current = b64;
    onChange(b64);
  }, [onChange]);

  const pushHistory = useCallback(() => {
    historyRef.current = [...historyRef.current.slice(-(MAX_UNDO - 1)), { px: new Uint8Array(pixelsRef.current), none: noneRef.current }];
    setHistLen(historyRef.current.length);
  }, []);

  const undo = useCallback(() => {
    const h = historyRef.current;
    if (h.length === 0) return;
    const prev = h[h.length - 1];
    historyRef.current = h.slice(0, -1);
    setHistLen(historyRef.current.length);
    if (prev.none) {
      setPixels(new Uint8Array(prev.px));
      emittedRef.current = null;
      onChange(null);
    } else {
      commit(new Uint8Array(prev.px));
    }
  }, [commit, onChange]);

  const chooseTool = (t: Tool) => {
    setTool(t);
    if (t !== 'select') setSel(null);
  };

  // Raccourcis : hors champ de saisie, et seulement si aucune modale n'est ouverte par-dessus.
  useEffect(() => {
    if (readOnly) return;
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement | null;
      if (t && (t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.tagName === 'SELECT' || t.isContentEditable)) return;
      if (!editorOnTop(rootRef.current)) return;
      if ((e.ctrlKey || e.metaKey) && !e.shiftKey && e.key.toLowerCase() === 'z') { e.preventDefault(); undo(); return; }
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const k = e.key.toLowerCase();
      const hit = TOOLS.find((x) => x.shortcut.toLowerCase() === k);
      if (hit) { e.preventDefault(); chooseTool(hit.key); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [readOnly, undo]);

  const cellAt = (e: React.PointerEvent): Cell | null => {
    const el = gridRef.current;
    if (!el) return null;
    const rect = el.getBoundingClientRect();
    const c = Math.floor(((e.clientX - rect.left) / rect.width) * FLAG_W);
    const r = Math.floor(((e.clientY - rect.top) / rect.height) * FLAG_H);
    if (c < 0 || r < 0 || c >= FLAG_W || r >= FLAG_H) return null;
    return { c, r };
  };

  const paintLine = (from: Cell | null, to: Cell) => {
    const next = new Uint8Array(pixelsRef.current);
    const a = from ?? to;
    bresenham(a.c, a.r, to.c, to.r, (c, r) => { next[r * FLAG_W + c] = color; });
    commit(next);
  };

  const bucket = (c: number, r: number) => {
    const src = pixelsRef.current;
    const target = src[r * FLAG_W + c];
    if (target === color) return;
    pushHistory();
    const next = new Uint8Array(src);
    const stack = [r * FLAG_W + c];
    while (stack.length) {
      const i = stack.pop()!;
      if (next[i] !== target) continue;
      next[i] = color;
      const x = i % FLAG_W;
      const y = Math.floor(i / FLAG_W);
      if (x > 0) stack.push(i - 1);
      if (x < FLAG_W - 1) stack.push(i + 1);
      if (y > 0) stack.push(i - FLAG_W);
      if (y < FLAG_H - 1) stack.push(i + FLAG_W);
    }
    commit(next);
  };

  const onDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (readOnly || e.button !== 0) return;
    const cell = cellAt(e);
    if (!cell) return;
    e.preventDefault();
    if (tool === 'pick') {
      setColor(pixelsRef.current[cell.r * FLAG_W + cell.c]);
      setTool('brush');
      return;
    }
    if (tool === 'bucket') { bucket(cell.c, cell.r); return; }
    e.currentTarget.setPointerCapture(e.pointerId);
    if (tool === 'select') {
      setSel({ c0: cell.c, r0: cell.r, c1: cell.c, r1: cell.r });
      dragRef.current = { last: cell, selecting: true };
      return;
    }
    pushHistory();
    dragRef.current = { last: cell, selecting: false };
    paintLine(null, cell);
  };

  const onMove = (e: React.PointerEvent<HTMLDivElement>) => {
    const cell = cellAt(e);
    setHover((h) => (h && cell && h.c === cell.c && h.r === cell.r ? h : cell));
    const d = dragRef.current;
    if (!d || !cell || (d.last && d.last.c === cell.c && d.last.r === cell.r)) return;
    if (d.selecting) setSel((s) => (s ? { ...s, c1: cell.c, r1: cell.r } : s));
    else paintLine(d.last, cell);
    d.last = cell;
  };

  const onUp = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!dragRef.current) return;
    dragRef.current = null;
    if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
  };

  const pickPalette = (i: number) => {
    setColor(i);
    if (readOnly || !sel) return;
    // Sélection active : le clic dans la palette la remplit.
    const s = normSel(sel);
    pushHistory();
    const next = new Uint8Array(pixelsRef.current);
    for (let r = s.r0; r <= s.r1; r++) for (let c = s.c0; c <= s.c1; c++) next[r * FLAG_W + c] = i;
    commit(next);
  };

  const blank = () => {
    pushHistory();
    commit(new Uint8Array(FLAG_SIZE).fill(FLAG_WHITE));
    setSel(null);
  };

  const clearFlag = () => {
    if (value === null) return;
    pushHistory();
    setPixels(new Uint8Array(FLAG_SIZE).fill(FLAG_WHITE));
    setSel(null);
    emittedRef.current = null;
    onChange(null);
  };

  const importImage = (file: File) => {
    if (!file.type.startsWith('image/')) {
      toast.error(`${file.name} n'est pas une image : choisis un PNG, un JPEG ou un GIF.`);
      return;
    }
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const cv = document.createElement('canvas');
      cv.width = FLAG_W;
      cv.height = FLAG_H;
      const ctx = cv.getContext('2d');
      if (ctx) {
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';
        ctx.drawImage(img, 0, 0, FLAG_W, FLAG_H);
        const data = ctx.getImageData(0, 0, FLAG_W, FLAG_H).data;
        const next = new Uint8Array(FLAG_SIZE);
        for (let i = 0; i < FLAG_SIZE; i++) next[i] = nearestPaletteIndex(data[i * 4], data[i * 4 + 1], data[i * 4 + 2]);
        pushHistory();
        commit(next);
        setSel(null);
        toast.success(`Image importée : ramenée en 26 x 13 sur les 18 couleurs de la palette.`);
      } else {
        toast.error('Import impossible : le navigateur refuse le dessin hors écran.');
      }
      URL.revokeObjectURL(url);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      toast.error(`${file.name} est illisible : fichier image corrompu ou format non pris en charge.`);
    };
    img.src = url;
  };

  const s = sel ? normSel(sel) : null;
  const hoverIdx = hover ? hover.r * FLAG_W + hover.c : -1;
  const isNone = value === null;

  const info = (() => {
    if (s) {
      return <>Sélection <strong>{s.c1 - s.c0 + 1} x {s.r1 - s.r0 + 1}</strong> cases{readOnly ? '' : ' : clique une couleur pour la remplir.'}</>;
    }
    if (hover) return <>Case {hover.c + 1}, {hover.r + 1} · <code>{FLAG_PALETTE[pixels[hoverIdx]]}</code></>;
    return readOnly ? null : 'Survole la grille pour lire une case.';
  })();

  return (
    <div ref={rootRef} className={ws.flagEditor}>
      {!readOnly && (
        <div className={ws.flagTools} role="toolbar" aria-label="Outils du drapeau">
          <div className={cs.toolGroup}>
            {TOOLS.map((t) => {
              const Icon = t.icon;
              const on = tool === t.key;
              return (
                <button key={t.key} type="button" title={`${t.label} (touche ${t.shortcut}) : ${t.effect}`}
                  aria-pressed={on} className={cx(ws.toolBtn, on && ws.toolBtnOn)} onClick={() => chooseTool(t.key)}>
                  <Icon size={14} aria-hidden /> <span>{t.label}</span> <span className={cs.toolKbd}><Kbd>{t.shortcut}</Kbd></span>
                </button>
              );
            })}
          </div>
          <span className={cx(ws.toolSep, cs.toolSepWide)} aria-hidden />
          <div className={cs.toolGroup}>
            <button type="button" className={styles.secondaryBtn} onClick={undo} disabled={histLen === 0}
              title={histLen ? 'Annuler la dernière action (Ctrl+Z)' : 'Rien à annuler'}>
              <Undo2 size={14} aria-hidden /> Annuler{histLen ? ` (${histLen})` : ''}
            </button>
            <button type="button" className={styles.secondaryBtn} onClick={() => fileRef.current?.click()}
              title="Importer une image : étirée en 26 x 13, chaque case ramenée à la couleur de palette la plus proche">
              <ImageUp size={14} aria-hidden /> Importer une image
            </button>
            <button type="button" className={cx(styles.secondaryBtn, cs.blankBtn)} onClick={blank}
              title="Tout repeindre en blanc (annulable)">
              <Trash2 size={14} aria-hidden /> Page blanche
            </button>
            {nullable && (
              <button type="button" className={styles.secondaryBtn} onClick={clearFlag} disabled={isNone}
                title={isNone ? "Le pays n'a déjà aucun drapeau" :'Retirer le drapeau du pays (blanc en jeu, annulable)'}>
                <ImageOff size={14} aria-hidden /> Aucun drapeau
              </button>
            )}
          </div>
          <input ref={fileRef} type="file" accept="image/*" hidden
            onChange={(e) => { const f = e.target.files?.[0]; if (f) importImage(f); e.target.value = ''; }} />
        </div>
      )}

      <div className={cx(ws.flagMain, cs.flagMainFit)}>
        <div className={ws.flagFrame} onPointerLeave={() => setHover(null)}>
          <div ref={gridRef} className={cx(ws.flagGrid, cs.gridSize, readOnly ? ws.flagGridRo : ws[`cur_${tool}`])}
            role={readOnly ? 'img' : undefined}
            aria-label={isNone ? 'Aucun drapeau défini : blanc en jeu' : `Drapeau du pays, 26 x 13 cases${readOnly ? '' : ', à peindre à la souris'}`}
            onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp}>
            {Array.from(pixels, (v, i) => {
              const c = i % FLAG_W;
              const r = Math.floor(i / FLAG_W);
              const inSel = !!s && c >= s.c0 && c <= s.c1 && r >= s.r0 && r <= s.r1;
              return (
                <span key={i} className={cx(inSel && ws.flagCellSel, i === hoverIdx && !readOnly && ws.flagCellHover)}
                  style={{ background: FLAG_PALETTE[v] }} />
              );
            })}
          </div>
        </div>
        <div className={cs.flagInfo} aria-live="polite">{info}</div>
      </div>

      {!readOnly && (
        <aside className={ws.flagSide}>
          <div>
            <p className={cs.sideTitle}>Couleur</p>
            <div className={ws.flagCurrent}>
              <span style={{ background: FLAG_PALETTE[color] }} aria-hidden />
              <code>{FLAG_PALETTE[color]}</code>
            </div>
          </div>
          <div>
            <p className={cs.sideTitle}>Palette ({FLAG_PALETTE.length} couleurs)</p>
            <div className={ws.flagPaletteGrid} role="group" aria-label="Palette du drapeau">
              {FLAG_PALETTE.map((hex, i) => (
                <button key={hex} type="button" title={`${hex}${sel ? ' : remplit la sélection' : ''}`}
                  aria-label={hex} aria-pressed={color === i}
                  className={cx(ws.flagSwatch, color === i && ws.flagSwatchOn)}
                  style={{ background: hex }} onClick={() => pickPalette(i)} />
              ))}
            </div>
            {sel && <p className={cs.selTip}>Astuce : clique une couleur pour remplir la sélection.</p>}
          </div>
          <ul className={ws.flagLegend} aria-label="Outils">
            {TOOLS.map((t) => {
              const Icon = t.icon;
              return (
                <li key={t.key}>
                  <Icon size={13} aria-hidden />
                  <Kbd>{t.shortcut}</Kbd>
                  <span><strong>{t.label}</strong> : {t.effect}</span>
                </li>
              );
            })}
            <li>
              <Undo2 size={13} aria-hidden />
              <Kbd>Ctrl+Z</Kbd>
              <span><strong>Annuler</strong> : {MAX_UNDO} pas au plus.</span>
            </li>
          </ul>
        </aside>
      )}
    </div>
  );
}
